class CustomPredictiveSearch {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('[data-predictive-search-input]');
    this.resetButton = wrapper.querySelector('[data-predictive-search-reset]');
    this.statusEl = wrapper.querySelector('[data-predictive-search-status]');
    this.suggestionsPanel = wrapper.querySelector('[data-predictive-search-suggestions]');
    this.productsPanel = wrapper.querySelector('[data-predictive-search-products]');

    if (!this.input || !this.suggestionsPanel || !this.productsPanel) return;

    this.minChars = 2;
    this.debounceMs = 200;
    this.resultLimit = 8;
    this.viewAllThreshold = 4;
    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;
    this.currentTerm = '';

    // Preserve the empty-state markup (Trending / Most Popular) so we can restore it
    this.defaultSuggestionsHTML = this.suggestionsPanel.innerHTML;
    this.defaultProductsHTML = this.productsPanel.innerHTML;

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onInput.bind(this));

    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.clearSearch());
      this.toggleResetButton();
    }

    // Trending/suggestions pill slider: Swiper on mobile only, plain list at 750px+
    this.desktopMql = window.matchMedia('(min-width: 750px)');
    this.desktopMql.addEventListener('change', () => this.initTrendingSliders(this.suggestionsPanel));
    this.initTrendingSliders(this.suggestionsPanel);
  }

  initTrendingSliders(container) {
    if (!container) return;

    container.querySelectorAll('[data-trending-slider]').forEach((wrapper) => {
      const swiperEl = wrapper.querySelector('.search-modal__trending-swiper');
      const fillEl = wrapper.querySelector('[data-trending-progress-fill]');
      if (!swiperEl) return;

      if (swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
        swiperEl.swiper = null;
      }

      // Desktop: leave as a plain stacked list, no Swiper instance
      if (this.desktopMql.matches || typeof window.Swiper === 'undefined') return;

      swiperEl.swiper = new window.Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        slidesOffsetAfter: 50,
        freeMode: true,
        grabCursor: true,
        watchOverflow: true,
        on: {
          progress(swiper) {
            if (!fillEl) return;
            const progress = Math.min(Math.max(swiper.progress, 0), 1) || 0;
            fillEl.style.transform = `scaleX(${Math.max(progress, 0.08)})`;
          },
        },
      });
    });
  }

  onInput(event) {
    const term = event.target.value.trim();
    clearTimeout(this.debounceTimer);
    this.toggleResetButton();

    if (term.length === 0) {
      this.reset();
      return;
    }

    if (term.length < this.minChars) return;

    this.debounceTimer = setTimeout(() => this.search(term), this.debounceMs);
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    this.resetButton.classList.toggle('hidden', this.input.value.trim().length === 0);
  }

  clearSearch() {
    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.reset();
  }

  reset() {
    if (this.abortController) this.abortController.abort();
    this.suggestionsPanel.innerHTML = this.defaultSuggestionsHTML;
    this.productsPanel.innerHTML = this.defaultProductsHTML;
    this.setStatus('');
    this.initTrendingSliders(this.suggestionsPanel);
  }

  async search(term) {
    this.currentTerm = term;

    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setLoading(true);

    const baseUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest.json';
    const url = `${baseUrl}?q=${encodeURIComponent(term)}&resources[type]=product,query,collection`
      + `&resources[limit]=${this.resultLimit}`
      + `&resources[options][unavailable_products]=last`
      + `&section_id=predictive-search`;

    const searchBase = (window.routes && window.routes.search_url) || '/search';
    const countUrl = `${searchBase}?q=${encodeURIComponent(term)}&view=json&type=product`;

    try {
      const [suggestResponse, countResponse] = await Promise.all([
        fetch(url, { signal: this.abortController.signal, headers: { Accept: 'application/json' } }),
        fetch(countUrl, { signal: this.abortController.signal, headers: { Accept: 'application/json' } }).catch(() => null),
      ]);

      if (!suggestResponse.ok) throw new Error(suggestResponse.statusText);

      const data = await suggestResponse.json();

      let totalCount = null;
      if (countResponse && countResponse.ok) {
        try {
          const countData = await countResponse.json();
          totalCount = typeof countData.count === 'number' ? countData.count : null;
        } catch (e) {
          totalCount = null;
        }
      }

      data.totalCount = totalCount;
      this.cache.set(term, data);
      this.render(data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search request failed:', error);
        this.renderError();
      }
    } finally {
      this.setLoading(false);
    }
  }

  render(data) {
    const results = data && data.resources && data.resources.results ? data.resources.results : {};
    const products = results.products || [];
    const totalCount = typeof data.totalCount === 'number' ? data.totalCount : products.length;

    this.renderSuggestions(results.queries || [], results.collections || []);
    this.renderProducts(products, this.currentTerm, totalCount);

    this.setStatus(`${totalCount} product${totalCount === 1 ? '' : 's'} found`);
  }

  renderSuggestions(queries, collections) {
    if (!queries.length && !collections.length) {
      this.suggestionsPanel.innerHTML = '<p class="search-modal__sidebar-heading">No suggestions found</p>';
      return;
    }

    let html = '';

    if (queries.length) {
      html += '<p class="search-modal__sidebar-heading">Suggestions</p>';
      // styled_text is Shopify-generated markup (wraps the matched term in <b>), used as-is
      html += this.buildTrendingSliderMarkup(queries.map((query) => ({ url: query.url, label: query.styled_text })));
    }

    if (collections.length) {
      html += '<p class="search-modal__sidebar-heading">Collections</p>';
      html += this.buildTrendingSliderMarkup(collections.map((collection) => ({ url: collection.url, label: this.escapeHTML(collection.title) })));
    }

    this.suggestionsPanel.innerHTML = html;
    this.initTrendingSliders(this.suggestionsPanel);
  }

  buildTrendingSliderMarkup(items) {
    let html = '<div class="search-modal__trending-slider" data-trending-slider>';
    html += '<div class="swiper search-modal__trending-swiper"><ul class="swiper-wrapper search-modal__trending-list" role="list">';
    items.forEach((item) => {
      html += `<li class="swiper-slide search-modal__trending-slide"><a href="${item.url}" class="search-modal__trending-link">${item.label}</a></li>`;
    });
    html += '</ul></div>';
    html += '<div class="search-modal__trending-progress" data-trending-progress><span class="search-modal__trending-progress-fill" data-trending-progress-fill></span></div>';
    html += '</div>';
    return html;
  }

  renderProducts(products, term, totalCount) {
    if (!products.length) {
      this.productsPanel.innerHTML = '<p class="search-modal__popular-heading">No products found</p>';
      return;
    }

    const count = typeof totalCount === 'number' ? totalCount : products.length;
    const showViewAll = count > this.viewAllThreshold;
    const visibleProducts = products.slice(0, this.viewAllThreshold);
    const searchUrl = `${(window.routes && window.routes.search_url) || '/search'}?q=${encodeURIComponent(term || '')}`;

    let html = `
      <div class="search-modal__results-header">
        <p class="search-modal__popular-heading">Results (${count})</p>
        ${showViewAll ? `
          <a href="${searchUrl}" class="search-modal__view-all">
            View all
            <span class="search-modal__view-all-icon" aria-hidden="true">&#8594;</span>
          </a>
        ` : ''}
      </div>
      <div class="search-modal__popular-grid">`;

    visibleProducts.forEach((product) => {
      html += `
        <a href="${product.url}" class="search-modal__popular-item">
          <div class="search-modal__popular-image">
            ${product.image ? `<img src="${product.image}" alt="${this.escapeHTML(product.title)}" loading="lazy" width="400" height="400">` : ''}
          </div>
          <p class="search-modal__popular-title">${this.escapeHTML(product.title)}</p>
        </a>`;
    });

    html += '</div>';
    this.productsPanel.innerHTML = html;
  }

  renderError() {
    this.suggestionsPanel.innerHTML = '<p class="search-modal__sidebar-heading">Something went wrong. Please try again.</p>';
    this.productsPanel.innerHTML = '';
  }

  setLoading(isLoading) {
    this.suggestionsPanel.classList.toggle('is-loading', isLoading);
    this.productsPanel.classList.toggle('is-loading', isLoading);
  }

  setStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }

  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

document.querySelectorAll('[data-custom-predictive-search]').forEach((wrapper) => {
  new CustomPredictiveSearch(wrapper);
});
